import type {
    Award,
    Faq,
    Principle,
    ProcessStep,
    Service,
    TeamMember
} from '@/types'

import { media, img } from '@/constants/media'

export const team: TeamMember[] = [
    { name: 'Jin Yuen', role: 'Founder', image: img('team-jin.jpg') },
    { name: 'Sarah Park', role: 'Designer', image: img('team-sarah.jpg') },
    { name: 'Alex West', role: 'Client Manager', image: img('team-alex.jpg') },
    { name: 'David Torres', role: 'Developer', image: img('team-david.jpg') }
]

export const services: Service[] = [
    {
        index: '(01)',
        title: 'Brand Identity.',
        price: 'Starts at $4,500',
        description: 'Logo, type, color, and the system that keeps it consistent everywhere it lives.',
        delivery: '2-3 weeks',
        includes: ['Logo and wordmark', 'Typography system', 'Color palette', 'Brand guidelines'],
        image: img('service-brand.jpg')
    },
    {
        index: '(02)',
        title: 'Web Design.',
        price: 'Starts at $6,500',
        description: 'Sites built to turn attention into enquiries, not just look good.',
        delivery: '3-4 weeks',
        includes: ['Up to 6 unique page designs', 'Mobile and tablet layouts', 'Component library', 'Hand-off documentation'],
        image: img('service-web.jpg')
    },
    {
        index: '(03)',
        title: 'Modulify Dev.',
        price: 'Starts at $4,000',
        description: 'Fast, animated, and easy for you to update yourself.',
        delivery: '2-3 weeks',
        includes: ['Pixel-perfect build in Modulify', 'CMS setup and migration', 'Responsive across breakpoints', '30-day post-launch support'],
        image: img('service-dev.jpg')
    },
    {
        index: '(04)',
        title: 'Art Direction.',
        price: 'Starts at $3,500',
        description: 'Photography, motion, and the visual language that ties everything together.',
        delivery: '2 weeks',
        includes: ['Mood and direction boards', 'Shoot or motion brief', 'Asset selection and edit', 'Usage guidelines'],
        image: img('service-art.jpg')
    }
]

export const processSteps: ProcessStep[] = [
    {
        index: '(01)',
        title: 'Discovery.',
        copy: "One call, one sharp brief. Your goals, your audience, and what's in the way.",
        image: img('process-discovery.png'),
        duration: 'Week 1',
        items: ['Kickoff call', 'Audience and market read', 'Audit of what you have', 'Written brief you sign off']
    },
    {
        index: '(02)',
        title: 'Strategy.',
        copy: 'One creative direction, argued for and agreed — before a single pixel moves.',
        image: img('process-strategy.png'),
        duration: 'Week 2',
        items: ['Positioning and voice', 'One art direction, not three', 'Messaging framework', 'Sitemap and page intent']
    },
    {
        index: '(03)',
        title: 'Design.',
        copy: 'Brand and site designed, then built. Three rounds, sharp ones.',
        image: img('process-design.png'),
        duration: 'Weeks 3 to 5',
        items: ['Identity system', 'Every page designed', 'Build and motion pass', 'Three rounds of revisions']
    },
    {
        index: '(04)',
        title: 'Launch.',
        copy: 'We ship, hand over the keys, and stay close for thirty days after go-live.',
        image: img('process-launch.png'),
        duration: 'Week 6',
        items: ['Testing across devices', 'Analytics and metadata', 'Every file and license handed over', 'Thirty days of support']
    }
]

export const principles: Principle[] = [
    {
        index: '01',
        title: 'You talk to the maker.',
        copy: 'No account layer, no handoffs. The person in your calls is the person in the file.'
    },
    {
        index: '02',
        title: 'Scope is set before we start.',
        copy: 'Price, timeline, and deliverables on paper before day one. Surprises are a design flaw.'
    },
    {
        index: '03',
        title: 'We say no early.',
        copy: 'If design is not your real problem, we will tell you in the first call and point you somewhere better.'
    },
    {
        index: '04',
        title: 'You own the work.',
        copy: 'Every file, font, and license comes with the handoff. Nothing stays hostage in our drive.'
    }
]

export const awards: Award[] = [
    { by: 'Awwwards', award: 'Site of the Day', year: '2026' },
    { by: 'FWA', award: 'FWA of the day', year: '2025' },
    { by: 'CSS design awards', award: 'Website of the day', year: '2024' },
    { by: 'Awwwards', award: 'Honorable Mention', year: '2023' }
]

export const studioNumbers = [
    { label: "industries we've shipped in", value: '12' },
    { label: 'people max on any project', value: '3' },
    { label: "longest you'll wait for a reply", value: '24hr' },
    { label: 'of our launches are still live', value: '92%' }
]

export const faqs: Faq[] = [
    {
        question: "What's the difference between monthly and per project?",
        answer: "A project has an end date. We build the brand and the site, hand it over, and it's yours. Membership is for what comes after, or instead of it. $4,500 a month keeps one active request moving, and you pause any month you don't need us."
    },
    {
        question: 'Is that the final price?',
        answer: "It's a real starting number, not a teaser. Scope is the only thing that moves it: an extra page, a second brand direction, a deadline that needs two of us. We lock the figure on the first call and it holds from there."
    },
    {
        question: 'How long will it take?',
        answer: 'Two to four weeks for a single piece of work, four to six for a full brand and site. We only take two clients a month, so your start date is real rather than a spot in a queue.'
    },
    {
        question: 'Who actually does the work?',
        answer: 'The founder. You hire Noma, you get Noma. No juniors, no handoffs, no account manager relaying your feedback.'
    },
    {
        question: 'What do you need from me to start?',
        answer: 'A short brief and someone who can make decisions. The clearer you are on the goal, the faster we move.'
    },
    {
        question: 'Do you offer support after launch?',
        answer: 'Every build ships with 30 days of post-launch support. After that, membership keeps us on call at $4,500 a month. Cancel it the month you stop needing us.'
    }
]

export const contactSteps = [
    {
        index: '01',
        title: 'You hear back within a day.',
        copy: 'From the founder, not a form-bot. We read every enquiry personally.'
    },
    {
        index: '02',
        title: 'A short discovery call.',
        copy: "Thirty minutes to understand your goals, audience, and what's not working."
    },
    {
        index: '03',
        title: 'A fixed-quote proposal.',
        copy: 'Clear scope, clear timeline, clear price. No surprises later.'
    }
]

export const addOns = [
    { label: 'Motion design', price: 1000 },
    { label: 'Illustration & brand assets', price: 800 },
    { label: 'Photography direction', price: 1200 },
    { label: 'Pitch deck or sales kit', price: 900 }
]

export const testimonials = [
    {
        quote: 'Noma finally has given us a site that shows the frames the way our customers already saw them.',
        body: "We used to explain what we were making. Noma built us a site that doesn't need explanation. It just shows what the frames are, and lets people fall for them.",
        name: 'Ines Costa',
        role: 'Founder, Kyma',
        avatar: media.clientPortraits[0],
        stats: [
            { label: 'sold out', value: '10 days' },
            { label: 'waitlist growth', value: '+240%' }
        ]
    },
    {
        quote: 'The first call rewrote the brief, and the brief was the whole problem.',
        body: 'We came in asking for a redesign and left with a much harder question about who we were actually for. Answering it took a week. Everything after that was fast.',
        name: 'Marc Oduya',
        role: 'Founder, Relay',
        avatar: media.clientPortraits[1],
        stats: [
            { label: 'qualified inbound', value: '+47%' },
            { label: 'to launch', value: '5 weeks' }
        ]
    },
    {
        quote: 'No juniors, no handoffs. The person on the call was the person in the file.',
        body: 'Every agency promises senior attention. This is the first time the work actually looked like one person made all of it, because one person did.',
        name: 'Hana Weber',
        role: 'Founder, Atlas Type',
        avatar: media.clientPortraits[2],
        stats: [
            { label: 'referrals sent', value: '6' },
            { label: 'still live', value: '3 years' }
        ]
    }
]
